import { DisplayState } from "@/js/app/enums";
import { PriceDisplay } from "./PriceDisplay";
import { Line } from "@/js/interfaces/DTOs/Models";

export class PriceDisplayBag {
    lines: Map<number, PriceDisplay> = new Map();
    private readonly lesserValueUnit: string;

    /**
     * Create a new PriceDisplayBag.
     *
     * @param lesserValueUnit The unit used to define the "lesser value".
     */
    constructor(lesserValueUnit: string) {
        this.lesserValueUnit = lesserValueUnit;
    }

    /**
     * Add a PriceDisplay for the given line.
     *
     * @param line The line to create the PriceDisplay for and add to the collection.
     */
    public add(line: Line): void {
        this.lines.set(line.id, this.createPriceDisplayMapping(line));
    }

    /**
     * Add PriceDisplays for the given lines.
     * @param lines The lines to create the PriceDisplays for and add to the collection.
     */
    public addMany(lines: Array<Line>): void {
        lines.forEach((line) => this.add(line));
    }

    /**
     * Check if the collection has a PriceDisplay for the given line id.
     *
     * @param lineId The id of the line to check.
     * @returns True if the collection has a PriceDisplay for the given line id, false otherwise.
     */
    public has(lineId: number): boolean {
        return this.lines.has(lineId);
    }

    /**
     * Get the PriceDisplay for the given line id. If the collection does not have a PriceDisplay for the given line id, an empty PriceDisplay is returned.
     *
     * @param lineId The id of the line to get the PriceDisplay for.
     * @returns The PriceDisplay for the given line id, or an empty PriceDisplay if the collection does not have a PriceDisplay for the given line id.
     */
    public get(lineId: number): PriceDisplay {
        const data = this.lines.get(lineId);
        return data === undefined
            ? this.createEmptyPriceDisplayMapping()
            : data;
    }

    /**
     * Create a PriceDisplay mapping for the given line.
     *
     * @param line The line to create the PriceDisplay for.
     * @returns The PriceDisplay for the given line.
     */
    private createPriceDisplayMapping(line: Line): PriceDisplay {
        // Set states from line informations
        const display = {
            label: DisplayState.hidden,
            supply: line.supply_available
                ? DisplayState.show
                : DisplayState.hidden,
            drop: line.drop_available ? DisplayState.show : DisplayState.hidden,
            drop_off: line.drop_off_available
                ? DisplayState.show
                : DisplayState.hidden,
        };

        // Some cases update display to have the label and supply display visible only.
        if (
            line.override_price ||
            line.support_required ||
            line.unit === this.lesserValueUnit
        ) {
            display.label = DisplayState.show;
            display.supply = DisplayState.show;
            display.drop = DisplayState.remove;
            display.drop_off = DisplayState.remove;
        }

        return new PriceDisplay(
            display.label,
            display.supply,
            display.drop,
            display.drop_off,
        );
    }

    /**
     * Create an empty PriceDisplay mapping, with all the fields set to hidden. This is used when we want to initialize a line that does not have any price display information, or when we want to reset the price display information of a line.
     *
     * @returns An empty PriceDisplay with all the fields set to hidden.
     */
    private createEmptyPriceDisplayMapping(): PriceDisplay {
        return new PriceDisplay(
            DisplayState.hidden,
            DisplayState.hidden,
            DisplayState.hidden,
            DisplayState.hidden,
        );
    }
}
